import { TIME_ZONE } from "./constants";

/** Times are stored as ISO-8601 UTC strings and displayed in Europe/Zagreb. */
export type IsoDateTime = string;
/** Calendar dates are `YYYY-MM-DD` strings (local Croatian date). */
export type IsoDate = string;

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function zonedParts(instant: Date | number | string, _tz: string = TIME_ZONE): ZonedParts {
  const d = instant instanceof Date ? instant : new Date(instant);
  const p: Record<string, number> = {};
  for (const part of partsFormatter.formatToParts(d)) {
    if (part.type !== "literal") p[part.type] = parseInt(part.value, 10);
  }
  return { year: p.year!, month: p.month!, day: p.day!, hour: p.hour! % 24, minute: p.minute!, second: p.second! };
}

/** Offset of Europe/Zagreb from UTC at the given instant, in minutes (+60 or +120). */
export function zagrebOffsetMinutes(instant: Date | number): number {
  const ms = instant instanceof Date ? instant.getTime() : instant;
  const p = zonedParts(ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

/** Convert a local Zagreb wall-clock time to a UTC Date. */
export function zagrebToUtc(date: IsoDate, hour = 0, minute = 0): Date {
  const [y, m, d] = date.split("-").map((n) => parseInt(n, 10)) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d, hour, minute);
  let offset = zagrebOffsetMinutes(guess);
  let utc = guess - offset * 60000;
  const offset2 = zagrebOffsetMinutes(utc);
  if (offset2 !== offset) {
    offset = offset2;
    utc = guess - offset * 60000;
  }
  return new Date(utc);
}

/** Local Zagreb calendar date of an instant, as `YYYY-MM-DD`. */
export function localDate(instant: Date | number | string): IsoDate {
  const p = zonedParts(instant);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split("-").map((n) => parseInt(n, 10)) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Whole days between two calendar dates (b − a). */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  const [y1, m1, d1] = a.split("-").map((n) => parseInt(n, 10)) as [number, number, number];
  const [y2, m2, d2] = b.split("-").map((n) => parseInt(n, 10)) as [number, number, number];
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/** "01.11.2026." — Croatian date format, used in both locales (spec §9). */
export function formatDate(instant: Date | number | string | null | undefined): string {
  if (instant === null || instant === undefined || instant === "") return "";
  if (typeof instant === "string" && /^\d{4}-\d{2}-\d{2}$/.test(instant)) {
    const [y, m, d] = instant.split("-");
    return `${d}.${m}.${y}.`;
  }
  const p = zonedParts(instant);
  return `${pad(p.day)}.${pad(p.month)}.${p.year}.`;
}

/** "01.11." — short date without year. */
export function formatShortDate(instant: Date | number | string | null | undefined): string {
  const full = formatDate(instant);
  return full ? full.slice(0, 6) : "";
}

/**
 * A period of days: "03.11.2026. – 04.11.2026.", or one date when both ends fall on the same day.
 * `compact` drops the first year when both dates share it and leaves out the spaces, so the
 * period stays one token on receipts: "03.11.–04.11.2026.".
 */
export function formatDateRange(
  from: Date | number | string | null | undefined,
  to: Date | number | string | null | undefined,
  opts: { compact?: boolean } = {},
): string {
  const a = formatDate(from);
  const b = formatDate(to);
  if (!a || !b || a === b) return a || b;
  if (!opts.compact) return `${a} – ${b}`;
  return a.slice(6) === b.slice(6) ? `${a.slice(0, 6)}–${b}` : `${a}–${b}`;
}

/** "14:32" in Zagreb. */
export function formatTime(instant: Date | number | string): string {
  const p = zonedParts(instant);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** "01.11.2026. 14:32" in Zagreb. */
export function formatDateTime(instant: Date | number | string | null | undefined): string {
  if (instant === null || instant === undefined || instant === "") return "";
  return `${formatDate(instant)} ${formatTime(instant)}`;
}

export function nowIso(): IsoDateTime {
  return new Date().toISOString();
}

export function isIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map((n) => parseInt(n, 10)) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/** Parse "dd.mm.yyyy." or "dd.mm.yyyy" or ISO into an ISO date; null if invalid. */
export function parseDate(input: string): IsoDate | null {
  const s = input.trim();
  if (isIsoDate(s)) return s;
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})\.?$/.exec(s);
  if (!m) return null;
  const iso = `${m[3]}-${pad(parseInt(m[2]!, 10))}-${pad(parseInt(m[1]!, 10))}`;
  return isIsoDate(iso) ? iso : null;
}

export const HOUR_MS = 3600_000;
export const DAY_MS = 86_400_000;
